import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ShieldCheck, Plus, Download, ExternalLink, Search,
  Award, Users, Loader2, Hash, Calendar, XCircle, CheckCircle,
  Mail, ListChecks, AlertTriangle
} from 'lucide-react';

const TRACK_OPTIONS = [
  'STEM Explorer', 'Category Pioneer', 'Knowledge Builder',
  'STEM Scholar', 'STEM Champion', 'STEM Readiness', 'Course Completion',
];

export default function AdminSTEMCertificatesPage() {
  const { api } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [learners, setLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    learner_id: '', track: 'STEM Readiness', score: '',
  });
  const [bulkData, setBulkData] = useState({
    selectedLearners: [], track: 'Course Completion', score: '', send_email: true,
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [certsRes, learnersRes] = await Promise.all([
        api.get('/admin/stem-certificates/'),
        api.get('/admin/stem-certificates/learners/'),
      ]);
      setCertificates(certsRes.data.certificates || []);
      setLearners(learnersRes.data || []);
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleIssue = async () => {
    if (!formData.learner_id || !formData.score) {
      toast.error('Please fill all required fields');
      return;
    }
    setIssuing(true);
    try {
      const res = await api.post('/admin/stem-certificates/issue/', {
        learner_id: formData.learner_id,
        track: formData.track,
        score: parseFloat(formData.score),
        origin_url: window.location.origin,
      });
      toast.success(`Certificate ${res.data.cert_id} issued! Email notification sent.`);
      setShowIssueDialog(false);
      setFormData({ learner_id: '', track: 'STEM Readiness', score: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to issue certificate');
    } finally {
      setIssuing(false);
    }
  };

  const handleBulkIssue = async () => {
    if (bulkData.selectedLearners.length === 0 || !bulkData.score) {
      toast.error('Select learners and enter a score');
      return;
    }
    setIssuing(true);
    try {
      const res = await api.post('/admin/stem-certificates/bulk-issue/', {
        learner_ids: bulkData.selectedLearners,
        track: bulkData.track,
        score: parseFloat(bulkData.score),
        send_email: bulkData.send_email,
        origin_url: window.location.origin,
      });
      const { count, errors } = res.data;
      toast.success(`${count} certificate(s) issued!${bulkData.send_email ? ' Email notifications sent.' : ''}`);
      if (errors?.length > 0) toast.warning(`${errors.length} failed`);
      setShowBulkDialog(false);
      setBulkData({ selectedLearners: [], track: 'Course Completion', score: '', send_email: true });
      fetchData();
    } catch (e) {
      toast.error('Bulk issuance failed');
    } finally {
      setIssuing(false);
    }
  };

  const handleRevoke = async (certId, currentStatus) => {
    const action = currentStatus === 'revoked' ? 'reactivate' : 'revoke';
    if (!window.confirm(`Are you sure you want to ${action} certificate ${certId}?`)) return;
    try {
      const res = await api.post(`/admin/stem-certificates/${certId}/revoke/`);
      toast.success(`Certificate ${certId} ${res.data.status}!`);
      fetchData();
    } catch (e) {
      toast.error(`Failed to ${action} certificate`);
    }
  };

  const downloadPdf = (certId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/platform/certificates/pdf/${certId}/`, '_blank');
  };

  const gradeColor = (grade) => {
    if (grade === 'A+') return 'bg-emerald-100 text-emerald-700';
    if (grade === 'A') return 'bg-blue-100 text-blue-700';
    if (grade === 'B+') return 'bg-indigo-100 text-indigo-700';
    if (grade === 'B') return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  };

  const toggleBulkLearner = (id) => {
    setBulkData(prev => ({
      ...prev,
      selectedLearners: prev.selectedLearners.includes(id)
        ? prev.selectedLearners.filter(l => l !== id)
        : [...prev.selectedLearners, id],
    }));
  };

  const filtered = certificates.filter(c =>
    !searchTerm ||
    c.holder_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cert_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.track?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64" data-testid="admin-stem-certs-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-stem-certificates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-indigo-600" />
            STEM Certificates
          </h1>
          <p className="text-muted-foreground text-sm">Issue, manage, and revoke blockchain-verified certificates</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowBulkDialog(true)} data-testid="bulk-issue-btn">
            <ListChecks className="h-4 w-4 mr-2" /> Bulk Issue
          </Button>
          <Button onClick={() => setShowIssueDialog(true)} data-testid="issue-cert-btn">
            <Plus className="h-4 w-4 mr-2" /> Issue Certificate
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-indigo-100">
              <ShieldCheck className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{certificates.length}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-100">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{certificates.filter(c => c.status === 'active').length}</p>
              <p className="text-sm text-muted-foreground">Active</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-red-100">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{certificates.filter(c => c.status === 'revoked').length}</p>
              <p className="text-sm text-muted-foreground">Revoked</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Users className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{new Set(certificates.map(c => c.holder_name)).size}</p>
              <p className="text-sm text-muted-foreground">Unique Holders</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Certificates Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Issued Certificates</CardTitle>
              <CardDescription>All blockchain-verified STEM certificates</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, ID, track..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-certs"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center py-12">
              <ShieldCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No certificates found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Certificate ID</TableHead>
                  <TableHead>Holder</TableHead>
                  <TableHead>Track</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((cert) => (
                  <TableRow key={cert.id} data-testid={`cert-row-${cert.cert_id}`} className={cert.status === 'revoked' ? 'opacity-50' : ''}>
                    <TableCell className="font-mono text-xs">{cert.cert_id}</TableCell>
                    <TableCell className="font-medium">{cert.holder_name}</TableCell>
                    <TableCell>{cert.track}</TableCell>
                    <TableCell>{cert.score}%</TableCell>
                    <TableCell>
                      <Badge className={gradeColor(cert.grade)}>{cert.grade}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(cert.issued_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={cert.status === 'active' ? 'default' : 'destructive'}
                             className={cert.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {cert.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => downloadPdf(cert.cert_id)}
                                title="Download PDF" data-testid={`download-pdf-${cert.cert_id}`}>
                          <Download className="h-4 w-4" />
                        </Button>
                        {cert.verification_url && (
                          <Button variant="ghost" size="sm" title="Verify"
                                  onClick={() => window.open(cert.verification_url, '_blank')}>
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost" size="sm"
                          title={cert.status === 'revoked' ? 'Reactivate' : 'Revoke'}
                          onClick={() => handleRevoke(cert.cert_id, cert.status)}
                          data-testid={`revoke-btn-${cert.cert_id}`}
                          className={cert.status === 'revoked' ? 'text-emerald-600 hover:text-emerald-700' : 'text-red-500 hover:text-red-700'}
                        >
                          {cert.status === 'revoked' ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Issue Single Certificate Dialog */}
      <Dialog open={showIssueDialog} onOpenChange={setShowIssueDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
              Issue STEM Certificate
            </DialogTitle>
            <DialogDescription>Issue a single certificate. An email notification will be sent to the parent.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Learner *</Label>
              <Select value={formData.learner_id}
                      onValueChange={(v) => setFormData({...formData, learner_id: v})}>
                <SelectTrigger data-testid="learner-select">
                  <SelectValue placeholder="Choose a learner" />
                </SelectTrigger>
                <SelectContent>
                  {learners.map(l => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name} — {l.parent_email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Track *</Label>
              <Select value={formData.track}
                      onValueChange={(v) => setFormData({...formData, track: v})}>
                <SelectTrigger data-testid="track-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRACK_OPTIONS.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Score (0-100) *</Label>
              <Input type="number" min="0" max="100" placeholder="e.g. 85"
                     value={formData.score}
                     onChange={(e) => setFormData({...formData, score: e.target.value})}
                     data-testid="score-input" />
              <p className="text-xs text-muted-foreground">
                Grade: {formData.score ? (
                  parseFloat(formData.score) >= 95 ? 'A+' :
                  parseFloat(formData.score) >= 80 ? 'A' :
                  parseFloat(formData.score) >= 60 ? 'B+' :
                  parseFloat(formData.score) >= 40 ? 'B' : 'C'
                ) : '—'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIssueDialog(false)}>Cancel</Button>
            <Button onClick={handleIssue} disabled={issuing} data-testid="confirm-issue-btn">
              {issuing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              Issue Certificate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Issue Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-indigo-600" />
              Bulk Issue Certificates
            </DialogTitle>
            <DialogDescription>Select multiple learners to issue certificates at once. Email notifications will be sent to parents.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Track *</Label>
              <Select value={bulkData.track}
                      onValueChange={(v) => setBulkData({...bulkData, track: v})}>
                <SelectTrigger data-testid="bulk-track-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRACK_OPTIONS.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Score (0-100) *</Label>
              <Input type="number" min="0" max="100" placeholder="e.g. 85"
                     value={bulkData.score}
                     onChange={(e) => setBulkData({...bulkData, score: e.target.value})}
                     data-testid="bulk-score-input" />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="send-email"
                checked={bulkData.send_email}
                onCheckedChange={(v) => setBulkData({...bulkData, send_email: v})}
                data-testid="bulk-send-email"
              />
              <Label htmlFor="send-email" className="flex items-center gap-1.5 cursor-pointer">
                <Mail className="h-3.5 w-3.5" /> Send email notification to parents
              </Label>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Select Learners * ({bulkData.selectedLearners.length} selected)</Label>
                <Button variant="ghost" size="sm" onClick={() => setBulkData(p => ({
                  ...p, selectedLearners: p.selectedLearners.length === learners.length ? [] : learners.map(l => l.id)
                }))} data-testid="bulk-select-all">
                  {bulkData.selectedLearners.length === learners.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="border rounded-lg max-h-48 overflow-y-auto divide-y">
                {learners.map(l => (
                  <label key={l.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <Checkbox
                      checked={bulkData.selectedLearners.includes(l.id)}
                      onCheckedChange={() => toggleBulkLearner(l.id)}
                      data-testid={`bulk-learner-${l.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{l.name}</p>
                      <p className="text-xs text-muted-foreground">{l.parent_email} {l.grade ? `— Grade ${l.grade}` : ''}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkIssue} disabled={issuing || bulkData.selectedLearners.length === 0}
                    data-testid="confirm-bulk-issue-btn">
              {issuing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ListChecks className="h-4 w-4 mr-2" />}
              Issue {bulkData.selectedLearners.length} Certificate{bulkData.selectedLearners.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
